// i18n fragment - populated by the Phase 6 sweep. See ../strings.ts.
// Misc shared components: ToolPicker, LanguagePicker, ColorPicker,
// EmailAddressInput, WorkspaceUrlField, CustomLimitChip, ErrorState,
// PagerControl, PersonalTokensSection, AuthorizedAppsSection, Lightbox,
// SearchInput, SelectMenu, FilePreview, SqlitePreview, PdfPreview.
import type { LocaleStrings } from './base';

export const componentsMiscStrings: LocaleStrings = {
  en: {
    // ── ToolPicker.vue ──
    'toolPicker.all_installed':   'All installed available',
    'toolPicker.count_selected':  '{count} of {total} selected',
    'toolPicker.scope_note':      'This only sets which installed {plural} this agent can use. To install or remove {plural} for the whole workspace, go to',
    'toolPicker.manage':          'Manage {plural}',
    'toolPicker.clear':           'Clear',
    'toolPicker.clear_tip':       'Clear selection',
    'toolPicker.select_all':      'Select all',
    'toolPicker.select_all_tip':  'Make all installed {plural} available again',
    'toolPicker.empty':           'No {plural} installed at the workspace level yet.',
    'toolPicker.install':         'Install {plural}',
    'toolPicker.search':          'Search {plural}…',
    'toolPicker.clear_search':    'Clear search',
    'toolPicker.no_match':        "No {plural} match '{q}'.",
    'toolPicker.hint':            'All installed {plural} are available to this agent. Uncheck any to limit it to a specific set.',

    // ── LanguagePicker.vue ──
    'langPicker.make_default':    'Make default',
    'langPicker.default_tag':     'Default',
    'langPicker.add_more':        'Add another language…',
    'langPicker.search':          'Search languages…',
    'langPicker.list_aria':       'Language options',
    'langPicker.add_custom':      'Add "{code}"',
    'langPicker.none_before':     "No language matches '{q}'. Try a name (English or native) or a BCP-47 code like ",
    'langPicker.none_mid':        ' or ',
    'langPicker.none_after':      '.',

    // ── ColorPicker.vue ──
    'colorPicker.aria':           'Color',
    'colorPicker.set':            'Set color',
    'colorPicker.hex_aria':       'Hex color value',
    'colorPicker.sv_aria':        'Saturation and brightness',
    'colorPicker.hue_aria':       'Hue',
    'colorPicker.clear':          'Clear',

    // ── EmailAddressInput.vue ──
    'emailInput.placeholder':     'localpart',

    // ── WorkspaceUrlField.vue ──
    'wsUrl.handle_aria':          'Workspace handle',
    'wsUrl.taken':                'That handle is taken.',
    'wsUrl.suggest_before':      'Try ',
    'wsUrl.suggest_after':        ' instead',
    'wsUrl.reserved':             'That handle is reserved.',
    'wsUrl.format':               'Use lowercase letters, numbers, and hyphens only.',
    'wsUrl.too_short':            'A handle needs at least 3 characters.',
    'wsUrl.preview_before':       'Your workspace will live at ',
    'wsUrl.preview_mid':          ' and email arrives at ',
    'wsUrl.preview_after':        '.',
    'wsUrl.saved':                'Saved',
    'wsUrl.change':               'Change handle',
    'wsUrl.update_error':         'Could not update the handle',

    // ── CustomLimitChip.vue ──
    'customLimit.per_day':        '{count}/day',
    'customLimit.none':           'No daily cap',
    'customLimit.edit_tip':       'Edit daily call limit',
    'customLimit.prompt_title':   'Daily call limit',
    'customLimit.prompt_body':    'Cap how many calls {name} can make per day. Leave blank for no cap.',
    'customLimit.prompt_label':   'Calls per day',
    'customLimit.prompt_placeholder': 'e.g. 500',
    'customLimit.err_number':     'Enter a whole number.',
    'customLimit.err_zero':       'Use a number above 0, or leave blank for no cap.',
    'customLimit.removed':        'Daily cap removed',
    'customLimit.set':            'Daily cap set to {count}',
    'customLimit.error':          'Could not update the limit',

    // ── ErrorState.vue ──
    'errorState.title':           'Something went wrong',
    'errorState.retry':           'Try again',

    // ── PagerControl.vue ──
    'pager.aria':                 'Pagination',
    'pager.prev':                 'Previous',
    'pager.page_of':              'Page {page} of {total}',

    // ── PersonalTokensSection.vue ──
    'tokens.title':               'Personal access tokens',
    'tokens.new':                 'New token',
    'tokens.lede_before':         'Use a token in the ',
    'tokens.lede_after':          ' header to call the API as yourself. ',
    'tokens.lede_register':       'Register an app',
    'tokens.lede_end':            ' for OAuth instead.',
    'tokens.reveal_warn':         'Copy this token now - it is shown only once.',
    'tokens.reveal_done':         'Done, I saved it',
    'tokens.name_hint':           'A label so you recognize this token later.',
    'tokens.name_placeholder':    'e.g. CI pipeline',
    'tokens.scopes_label':        'Scopes',
    'tokens.scopes_hint':         'What this token may do. It can never exceed your own role.',
    'tokens.expiry_label':        'Expires',
    'tokens.expiry_30d':          '30 days',
    'tokens.expiry_90d':          '90 days',
    'tokens.expiry_1y':           '1 year',
    'tokens.expiry_none':         'No expiry',
    'tokens.creating':            'Creating…',
    'tokens.create':              'Create token',
    'tokens.create_error':        'Could not create the token',
    'tokens.created_toast':       'Token created',
    'tokens.copy_error':          'Could not copy the token',
    'tokens.empty':               'No personal access tokens yet.',
    'tokens.created':             'Created {date}',
    'tokens.last_used':           'Last used {date}',
    'tokens.never_used':          'Never used',
    'tokens.expires':             'Expires {date}',
    'tokens.no_expiry':           'No expiry',
    'tokens.revoke':              'Revoke',
    'tokens.revoke_tip':          'Revoke this token',
    'tokens.revoke_title':        'Revoke token',
    'tokens.revoke_body':         'Revoke "{name}"? Anything using it stops working immediately.',
    'tokens.revoke_error':        'Could not revoke the token',
    'tokens.revoked_toast':       'Token revoked',

    // ── AuthorizedAppsSection.vue ──
    'authorizedApps.title':       'Authorized apps',
    'authorizedApps.lede':        'Third-party apps you have allowed to act on your behalf. Revoking drops the consent and kills the app\'s live tokens at once.',
    'authorizedApps.empty':       'No apps authorized yet.',
    'authorizedApps.disabled_by_dev': 'Disabled by developer',
    'authorizedApps.authorized':  'Authorized {date}',
    'authorizedApps.revoke':      'Revoke',
    'authorizedApps.revoke_btn':  'Revoke',
    'authorizedApps.revoke_tip':  'Revoke access',
    'authorizedApps.revoke_title':'Revoke access',
    'authorizedApps.revoke_body': 'Revoke access for "{name}"? It will stop acting on your behalf immediately.',
    'authorizedApps.revoke_error':'Could not revoke access',
    'authorizedApps.revoked':     'Access revoked',
    'authorizedApps.revoked_with_tokens_one':   'Access revoked, 1 token killed',
    'authorizedApps.revoked_with_tokens_other': 'Access revoked, {count} tokens killed',

    // ── Lightbox.vue ──
    'lightbox.viewer_aria':       'Image viewer',
    'lightbox.download':          'Download',
    'lightbox.download_aria':     'Download image',

    // ── SearchInput.vue ──
    'searchInput.clear':          'Clear',
    'searchInput.clear_aria':     'Clear search',

    // ── SelectMenu.vue ──
    'selectMenu.placeholder':     'Select…',
    'selectMenu.search':          'Search',
    'selectMenu.search_aria':     'Search {label}',
    'selectMenu.no_matches':      'No matches',

    // ── FilePreview.vue ──
    'filePreview.load_error':     'Failed to load (HTTP {status})',
    'filePreview.expand_tip':     'Expand',
    'filePreview.view_source':    'View source',
    'filePreview.view_rendered':  'View rendered',
    'filePreview.source':         'Source',
    'filePreview.no_preview':     'No preview for this file type.',
    'filePreview.download':       'Download',

    // ── SqlitePreview.vue ──
    'sqlitePreview.tables':       'Tables',
    'sqlitePreview.no_tables':    'No tables.',
    'sqlitePreview.asleep_schema':'Workspace is asleep - reopen it to read the schema.',
    'sqlitePreview.schema_failed':'Could not read the schema (HTTP {status}).',
    'sqlitePreview.asleep_query': 'Workspace is asleep - reopen it to run queries.',
    'sqlitePreview.query_failed': 'Query failed (HTTP {status}).',
    'sqlitePreview.run':          'Run',
    'sqlitePreview.write_tip':    'Allow writes (INSERT / UPDATE / DELETE / DDL)',
    'sqlitePreview.read_write':   'Read + write',
    'sqlitePreview.read_only':    'Read-only',
    'sqlitePreview.stat_rows':    '{count} rows{capped} in {ms} ms',
    'sqlitePreview.capped_suffix':' (capped)',
    'sqlitePreview.stat_affected':'{count} rows affected in {ms} ms',
    'sqlitePreview.copy_csv':     'Copy as CSV',
    'sqlitePreview.copy_json':    'Copy as JSON',
    'sqlitePreview.copied':       'Copied as {format}',
    'sqlitePreview.copy_failed':  'Could not copy',
    'sqlitePreview.truncated_before': 'Showing the first {count} rows. Add a ',
    'sqlitePreview.truncated_after':  ' to narrow the result.',
    'sqlitePreview.hint':         'Write a query and press Run to see results.',

    // ── PdfPreview.vue ──
    'pdfPreview.zoom_out':        'Zoom out',
    'pdfPreview.zoom_in':         'Zoom in',
    'pdfPreview.fit_width':       'Fit width',
    'pdfPreview.find':            'Find',
    'pdfPreview.download':        'Download',
    'pdfPreview.download_aria':   'Download {name}',
    'pdfPreview.pdf_fallback':    'PDF',
    'pdfPreview.find_placeholder':'Find in document…',
    'pdfPreview.no_results':      'No results',
    'pdfPreview.prev_match':      'Previous match',
    'pdfPreview.next_match':      'Next match',
    'pdfPreview.rendering':       'Rendering PDF…',
    'pdfPreview.render_error':    'Could not render this PDF.',
    'pdfPreview.download_file':   'Download file',
    'pdfPreview.open':            'Open PDF',
    'pdfPreview.mobile_hint':     'Open the PDF in your browser to view it.',
  },
  sv: {
    // ── ToolPicker.vue ──
    'toolPicker.all_installed':   'Alla installerade tillgängliga',
    'toolPicker.count_selected':  '{count} av {total} valda',
    'toolPicker.scope_note':      'Det här anger bara vilka installerade {plural} den här agenten kan använda. För att installera eller ta bort {plural} för hela arbetsytan, gå till',
    'toolPicker.manage':          'Hantera {plural}',
    'toolPicker.clear':           'Rensa',
    'toolPicker.clear_tip':       'Rensa valet',
    'toolPicker.select_all':      'Markera alla',
    'toolPicker.select_all_tip':  'Gör alla installerade {plural} tillgängliga igen',
    'toolPicker.empty':           'Inga {plural} installerade på arbetsytenivå än.',
    'toolPicker.install':         'Installera {plural}',
    'toolPicker.search':          'Sök {plural}…',
    'toolPicker.clear_search':    'Rensa sökning',
    'toolPicker.no_match':        "Inga {plural} matchar '{q}'.",
    'toolPicker.hint':            'Alla installerade {plural} är tillgängliga för den här agenten. Avmarkera för att begränsa till en specifik uppsättning.',

    // ── LanguagePicker.vue ──
    'langPicker.make_default':    'Gör till standard',
    'langPicker.default_tag':     'Standard',
    'langPicker.add_more':        'Lägg till ett språk till…',
    'langPicker.search':          'Sök språk…',
    'langPicker.list_aria':       'Språkalternativ',
    'langPicker.add_custom':      'Lägg till "{code}"',
    'langPicker.none_before':     "Inget språk matchar '{q}'. Prova ett namn (engelskt eller på modersmålet) eller en BCP-47-kod som ",
    'langPicker.none_mid':        ' eller ',
    'langPicker.none_after':      '.',

    // ── ColorPicker.vue ──
    'colorPicker.aria':           'Färg',
    'colorPicker.set':            'Ange färg',
    'colorPicker.hex_aria':       'Hex-färgvärde',
    'colorPicker.sv_aria':        'Mättnad och ljushet',
    'colorPicker.hue_aria':       'Nyans',
    'colorPicker.clear':          'Rensa',

    // ── EmailAddressInput.vue ──
    'emailInput.placeholder':     'lokaldel',

    // ── WorkspaceUrlField.vue ──
    'wsUrl.handle_aria':          'Arbetsytans handtag',
    'wsUrl.taken':                'Det handtaget är upptaget.',
    'wsUrl.suggest_before':      'Prova ',
    'wsUrl.suggest_after':        ' istället',
    'wsUrl.reserved':             'Det handtaget är reserverat.',
    'wsUrl.format':               'Använd endast små bokstäver, siffror och bindestreck.',
    'wsUrl.too_short':            'Ett handtag behöver minst 3 tecken.',
    'wsUrl.preview_before':       'Din arbetsyta finns på ',
    'wsUrl.preview_mid':          ' och e-post landar på ',
    'wsUrl.preview_after':        '.',
    'wsUrl.saved':                'Sparat',
    'wsUrl.change':               'Byt handtag',
    'wsUrl.update_error':         'Det gick inte att uppdatera handtaget',

    // ── CustomLimitChip.vue ──
    'customLimit.per_day':        '{count}/dag',
    'customLimit.none':           'Inget dagstak',
    'customLimit.edit_tip':       'Redigera dagligt anropstak',
    'customLimit.prompt_title':   'Dagligt anropstak',
    'customLimit.prompt_body':    'Begränsa hur många anrop {name} får göra per dag. Lämna tomt för inget tak.',
    'customLimit.prompt_label':   'Anrop per dag',
    'customLimit.prompt_placeholder': 't.ex. 500',
    'customLimit.err_number':     'Ange ett heltal.',
    'customLimit.err_zero':       'Använd ett tal över 0, eller lämna tomt för inget tak.',
    'customLimit.removed':        'Dagstak borttaget',
    'customLimit.set':            'Dagstak satt till {count}',
    'customLimit.error':          'Det gick inte att uppdatera gränsen',

    // ── ErrorState.vue ──
    'errorState.title':           'Något gick fel',
    'errorState.retry':           'Försök igen',

    // ── PagerControl.vue ──
    'pager.aria':                 'Sidnavigering',
    'pager.prev':                 'Föregående',
    'pager.page_of':              'Sida {page} av {total}',

    // ── PersonalTokensSection.vue ──
    'tokens.title':               'Personliga åtkomsttoken',
    'tokens.new':                 'Ny token',
    'tokens.lede_before':         'Använd en token i ',
    'tokens.lede_after':          '-headern för att anropa API:et som dig själv. ',
    'tokens.lede_register':       'Registrera en app',
    'tokens.lede_end':            ' för OAuth istället.',
    'tokens.reveal_warn':         'Kopiera den här token nu - den visas bara en gång.',
    'tokens.reveal_done':         'Klart, jag sparade den',
    'tokens.name_hint':           'En etikett så att du känner igen den här token senare.',
    'tokens.name_placeholder':    't.ex. CI-pipeline',
    'tokens.scopes_label':        'Behörigheter',
    'tokens.scopes_hint':         'Vad den här token får göra. Den kan aldrig överstiga din egen roll.',
    'tokens.expiry_label':        'Upphör',
    'tokens.expiry_30d':          '30 dagar',
    'tokens.expiry_90d':          '90 dagar',
    'tokens.expiry_1y':           '1 år',
    'tokens.expiry_none':         'Upphör aldrig',
    'tokens.creating':            'Skapar…',
    'tokens.create':              'Skapa token',
    'tokens.create_error':        'Det gick inte att skapa token',
    'tokens.created_toast':       'Token skapad',
    'tokens.copy_error':          'Det gick inte att kopiera token',
    'tokens.empty':               'Inga personliga åtkomsttoken än.',
    'tokens.created':             'Skapad {date}',
    'tokens.last_used':           'Senast använd {date}',
    'tokens.never_used':          'Aldrig använd',
    'tokens.expires':             'Upphör {date}',
    'tokens.no_expiry':           'Upphör aldrig',
    'tokens.revoke':              'Återkalla',
    'tokens.revoke_tip':          'Återkalla den här token',
    'tokens.revoke_title':        'Återkalla token',
    'tokens.revoke_body':         'Återkalla "{name}"? Allt som använder den slutar fungera omedelbart.',
    'tokens.revoke_error':        'Det gick inte att återkalla token',
    'tokens.revoked_toast':       'Token återkallad',

    // ── AuthorizedAppsSection.vue ──
    'authorizedApps.title':       'Behöriga appar',
    'authorizedApps.lede':        'Tredjepartsappar du har gett tillåtelse att agera för din räkning. Att återkalla tar bort samtycket och dödar appens aktiva token direkt.',
    'authorizedApps.empty':       'Inga appar behöriga än.',
    'authorizedApps.disabled_by_dev': 'Inaktiverad av utvecklaren',
    'authorizedApps.authorized':  'Behörig {date}',
    'authorizedApps.revoke':      'Återkalla',
    'authorizedApps.revoke_btn':  'Återkalla',
    'authorizedApps.revoke_tip':  'Återkalla åtkomst',
    'authorizedApps.revoke_title':'Återkalla åtkomst',
    'authorizedApps.revoke_body': 'Återkalla åtkomst för "{name}"? Den slutar agera för din räkning omedelbart.',
    'authorizedApps.revoke_error':'Det gick inte att återkalla åtkomsten',
    'authorizedApps.revoked':     'Åtkomst återkallad',
    'authorizedApps.revoked_with_tokens_one':   'Åtkomst återkallad, 1 token dödad',
    'authorizedApps.revoked_with_tokens_other': 'Åtkomst återkallad, {count} token dödade',

    // ── Lightbox.vue ──
    'lightbox.viewer_aria':       'Bildvisare',
    'lightbox.download':          'Ladda ner',
    'lightbox.download_aria':     'Ladda ner bild',

    // ── SearchInput.vue ──
    'searchInput.clear':          'Rensa',
    'searchInput.clear_aria':     'Rensa sökning',

    // ── SelectMenu.vue ──
    'selectMenu.placeholder':     'Välj…',
    'selectMenu.search':          'Sök',
    'selectMenu.search_aria':     'Sök {label}',
    'selectMenu.no_matches':      'Inga träffar',

    // ── FilePreview.vue ──
    'filePreview.load_error':     'Det gick inte att läsa in (HTTP {status})',
    'filePreview.expand_tip':     'Förstora',
    'filePreview.view_source':    'Visa källa',
    'filePreview.view_rendered':  'Visa renderat',
    'filePreview.source':         'Källa',
    'filePreview.no_preview':     'Ingen förhandsvisning för den här filtypen.',
    'filePreview.download':       'Ladda ner',

    // ── SqlitePreview.vue ──
    'sqlitePreview.tables':       'Tabeller',
    'sqlitePreview.no_tables':    'Inga tabeller.',
    'sqlitePreview.asleep_schema':'Arbetsytan sover - öppna den igen för att läsa schemat.',
    'sqlitePreview.schema_failed':'Det gick inte att läsa schemat (HTTP {status}).',
    'sqlitePreview.asleep_query': 'Arbetsytan sover - öppna den igen för att köra frågor.',
    'sqlitePreview.query_failed': 'Frågan misslyckades (HTTP {status}).',
    'sqlitePreview.run':          'Kör',
    'sqlitePreview.write_tip':    'Tillåt skrivningar (INSERT / UPDATE / DELETE / DDL)',
    'sqlitePreview.read_write':   'Läs + skriv',
    'sqlitePreview.read_only':    'Skrivskyddad',
    'sqlitePreview.stat_rows':    '{count} rader{capped} på {ms} ms',
    'sqlitePreview.capped_suffix':' (begränsat)',
    'sqlitePreview.stat_affected':'{count} rader påverkade på {ms} ms',
    'sqlitePreview.copy_csv':     'Kopiera som CSV',
    'sqlitePreview.copy_json':    'Kopiera som JSON',
    'sqlitePreview.copied':       'Kopierat som {format}',
    'sqlitePreview.copy_failed':  'Det gick inte att kopiera',
    'sqlitePreview.truncated_before': 'Visar de första {count} raderna. Lägg till ',
    'sqlitePreview.truncated_after':  ' för att begränsa resultatet.',
    'sqlitePreview.hint':         'Skriv en fråga och tryck på Kör för att se resultat.',

    // ── PdfPreview.vue ──
    'pdfPreview.zoom_out':        'Zooma ut',
    'pdfPreview.zoom_in':         'Zooma in',
    'pdfPreview.fit_width':       'Anpassa till bredd',
    'pdfPreview.find':            'Sök',
    'pdfPreview.download':        'Ladda ner',
    'pdfPreview.download_aria':   'Ladda ner {name}',
    'pdfPreview.pdf_fallback':    'PDF',
    'pdfPreview.find_placeholder':'Sök i dokumentet…',
    'pdfPreview.no_results':      'Inga träffar',
    'pdfPreview.prev_match':      'Föregående träff',
    'pdfPreview.next_match':      'Nästa träff',
    'pdfPreview.rendering':       'Renderar PDF…',
    'pdfPreview.render_error':    'Det gick inte att rendera den här PDF:en.',
    'pdfPreview.download_file':   'Ladda ner fil',
    'pdfPreview.open':            'Öppna PDF',
    'pdfPreview.mobile_hint':     'Öppna PDF:en i din webbläsare för att visa den.',
  },
};
